/**
 * The words on Settings: its sections, and the whole of the How it works
 * guide that is one of them. The source both apps copy character for
 * character (`SettingsCopy` on iOS and Android).
 *
 * **The guide states rules, it never makes them.** Every sentence below is a
 * rule already ruled in `docs/domain/01-discovery.md`, in the Consumer's words
 * rather than the glossary's: the one-hour Time Block (H39), the clinic's
 * manual approval (H7), expiry at the start (H3), the one-hour Cancellation
 * Window, the Reminder (H51), the Reschedule Suggestion and what rejecting one
 * does (H49), the Review Unlock (H13), the private Favorite (H35), My care
 * (H55). A rule that changes there changes here in the same change, and
 * `settingsCopy.test.ts` pins the numbers that would drift first.
 *
 * **No practitioner is named.** A Consumer books clinics, not people, and
 * learns who they are seeing once the Booking is Approved (H45), which is
 * exactly what the third step says.
 */

/**
 * Settings' sections, top to bottom. `appearance` and `shortcuts` exist only
 * in the apps: the web keeps the theme toggle and the bookings and inbox links
 * in its chrome, where every screen can reach them. `guide` holds How it works,
 * and sits above Session so the danger zone stays last (H34).
 */
export const SETTINGS_SECTIONS = {
  profile: {
    title: "Profile",
    detail: "Your name, and the number you log in with.",
  },
  security: {
    title: "Security",
    detail: "Change the password that protects your account.",
  },
  appearance: {
    title: "Appearance",
    detail: "Follow your phone, or choose light or dark.",
  },
  shortcuts: {
    title: "Shortcuts",
    detail: "Your appointments and your messages.",
  },
  guide: {
    title: "How it works",
    detail: "What Bookr does for you, step by step.",
  },
  session: {
    title: "Session",
    detail: "Log out of Bookr on this device.",
  },
  danger: {
    title: "Danger zone",
    detail: "Permanent. Read what happens before you go ahead.",
  },
} as const;

/** Where a step's button leads. Each platform maps these to its own screen. */
export type GuideDestination = "search" | "bookings" | "care";

export interface GuideStep {
  key: "find" | "request" | "confirm" | "before" | "after";
  title: string;
  summary: string;
  points: string[];
  action?: { label: string; to: GuideDestination };
}

export interface GuideFact {
  key: "saved" | "inbox" | "reviews" | "privacy";
  title: string;
  detail: string;
}

export const HOW_IT_WORKS = {
  eyebrow: "Your guide",
  title: "How Bookr works",
  intro:
    "From finding a clinic to the visit itself, in five steps. Here is everything Bookr does for you.",
  stepLabel: (n: number, total: number) => `Step ${n} of ${total}`,
  steps: [
    {
      key: "find",
      title: "Find care",
      summary: "Search clinics by specialty, distance, price and rating.",
      points: [
        "Type a clinic or a service, or pick a specialty.",
        "Filter by how far away, the starting price and the rating.",
        "Open a clinic to see its services, prices, hours and reviews.",
      ],
      action: { label: "Find care", to: "search" },
    },
    {
      key: "request",
      title: "Request a time",
      summary: "Choose a service, where it happens, and an hour that suits you.",
      points: [
        "Every appointment is one hour, starting on the hour.",
        "See it at the clinic, or at home where the clinic offers it.",
        "The price is shown before you ask, for the place you chose.",
      ],
    },
    {
      key: "confirm",
      title: "The clinic confirms",
      summary: "Your request waits for the clinic to say yes.",
      points: [
        "It shows as Awaiting confirmation until the clinic answers.",
        "Once it is confirmed, you see who you are seeing.",
        "A request nobody answers by its start time lapses on its own.",
        "You can withdraw a request at any time.",
      ],
      action: { label: "My bookings", to: "bookings" },
    },
    {
      key: "before",
      title: "Before your visit",
      summary: "Change the time or cancel, right from your bookings.",
      points: [
        "A WhatsApp reminder arrives up to a day ahead, with links to change or cancel.",
        "Pick another open hour yourself, or answer a time the clinic suggests.",
        "Turning down a suggested time cancels the appointment.",
        "You can cancel until 1 hour before the start. After that, call the clinic.",
      ],
    },
    {
      key: "after",
      title: "After your visit",
      summary: "Rate the visit, and keep what your clinic prepared for you.",
      points: [
        "You can leave a review 15 minutes after the appointment ends.",
        "Meal plans from your clinic appear in My care, to read or print.",
      ],
      action: { label: "My care", to: "care" },
    },
  ] satisfies GuideStep[],
  statusesTitle: "Your bookings at a glance",
  statusesIntro: "Each booking wears one of four colours.",
  factsTitle: "Good to know",
  facts: [
    {
      key: "saved",
      title: "Saved clinics",
      detail:
        "Tap the heart on a clinic to keep it. Your saved clinics open from the heart at the top right of Find care.",
    },
    {
      key: "inbox",
      title: "Your inbox",
      detail:
        "Every update about your bookings lands in your inbox. Confirmations, reminders and suggested times also come on WhatsApp.",
    },
    {
      key: "reviews",
      title: "Reviews you can trust",
      detail:
        "One review per appointment, and only for a visit that really happened.",
    },
    {
      key: "privacy",
      title: "Your privacy",
      detail:
        "A clinic is never told you saved it. Your phone number is how you log in, and it stays verified.",
    },
  ] satisfies GuideFact[],
  closingTitle: "Ready when you are.",
  closingAction: { label: "Find care", to: "search" as GuideDestination },
} as const;
